import { buildCollection, DataSourceDelegate, buildProperty, useDataSource, FireCMSContext, SelectionController, useSideEntityController } from "@firecms/core";
import { FieldSelect } from "../components/FieldSelect";
import { Button } from "@firecms/ui";
import { fieldCollection } from "./field";
import React, { useState, useCallback } from "react";
import { ImportDirectoriesModal } from "../components/ImportDirectoriesModal";
import { slugify, generatePath, generateTitle, generateDescription, generateMetaDescription, getLocationPathInput, getLocationDisplayName } from "../utils/pathGeneration";
import { LocationAttachmentField } from "../components/LocationAttachmentField";

const pathValidationTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export const directoryCollection = buildCollection<any>({
    id: "directory",
    name: "Directory",
    icon: "explore",
    description: "Manage and curate listicles for discovery and search boosting.",
    path: "directory",
    defaultSize: "xs",
    customId: false,
    properties: {
        filter_fields: {
            name: "Filter fields",
            dataType: "map",
            validation: { required: true },
            properties: {
                commitment_type: {
                    dataType: "string",
                    name: "Commitment type",
                    Field: FieldSelect,
                    customProps: { fieldId: "commitment_type" }
                },
                function: {
                    dataType: "string",
                    name: "Function",
                    Field: FieldSelect,
                    customProps: { fieldId: "function" }
                },
                function_focus: {
                    dataType: "string",
                    name: "Function focus",
                    Field: FieldSelect,
                    customProps: { fieldId: "function_focus", dependsOn: "function" }
                },
                industry: {
                    dataType: "string",
                    name: "Industry",
                    Field: FieldSelect,
                    // DR-398 curates industry options in key_value, not key_lookups
                    customProps: { fieldId: "industry", source: "key_value" }
                },
                // DR-408: map-valued location attachment, denormalised from
                // setting/directory/locations (all other filter keys stay strings)
                location: {
                    dataType: "map",
                    name: "Location",
                    Field: LocationAttachmentField,
                    previewProperties: ["name", "range"],
                    properties: {
                        ref: {
                            dataType: "reference",
                            path: "setting/directory/locations",
                            name: "Location ref"
                        },
                        name: { dataType: "string", name: "Name" },
                        latlng: { dataType: "geopoint", name: "Coordinates" },
                        range: { dataType: "number", name: "Range (miles)" },
                        // DR-447: city | state | country, copied from the
                        // location doc at attach time
                        type: { dataType: "string", name: "Type" }
                    }
                },
            }
        },
        override_clean_url: {
            dataType: "boolean",
            name: "Override Clean URL",
            defaultValue: false,
            hideFromCollection: true
        },
        clean_url: ({ values }) => {
            const filters = values.filter_fields || {};
            const generatedPath = generatePath(filters.commitment_type, filters.function_focus || filters.function, filters.industry, getLocationPathInput(filters.location));
            return {
                dataType: "string",
                name: "Clean URL",
                validation: {
                    required: true,
                    // @ts-ignore
                    validate: async ({ value, entityId, collection, context }) => {
                        if (!value) return true;
                        
                        return new Promise((resolve) => {
                            const timeoutKey = `${collection.path ?? "directory"}::${entityId ?? "new"}::${value}`;
                            const existingTimeout = pathValidationTimeouts.get(timeoutKey);
                            if (existingTimeout) clearTimeout(existingTimeout);
                            
                            const timeout = setTimeout(async () => {
                                try {
                                    const isUnique = await context.dataSource.checkUniqueField("directory", "clean_url", value, entityId, collection);
                                    resolve(isUnique ? true : "Clean URL already exists");
                                } catch (e) {
                                    resolve(true); 
                                } finally {
                                    pathValidationTimeouts.delete(timeoutKey);
                                }
                            }, 500);
                            
                            pathValidationTimeouts.set(timeoutKey, timeout);
                        });
                    }
                },
                readOnly: !values.override_clean_url,
                description: !values.override_clean_url ? `Auto-generated: ${generatedPath}` : "Manual override enabled",
            };
        },
        title: ({ values }) => {
            return {
                dataType: "string",
                name: "Title",
                validation: { required: true },
                description: "Auto-generated based on filter labels if left empty"
            };
        },
        description: {
            dataType: "string",
            name: "Description",
            multiline: true,
            description: "Auto-generated based on filter labels if left empty"
        },
        meta_title: {
            dataType: "string",
            name: "Meta title",
        },
        meta_description: {
            dataType: "string",
            name: "Meta description",
            multiline: true,
        },
        enabled: {
            dataType: "boolean",
            name: "Enabled"
        },
        visible_in_menu: {
            dataType: "boolean",
            name: "Visible in menu",
            description: "Determines if a directory page is visible in the main menu"
        },
        page_size: {
            dataType: "number",
            name: "Page size"
        },
        query: {
            dataType: "string",
            name: "Query",
            hideFromCollection: true
        },
        filter: {
            dataType: "string",
            name: "Filter",
            hideFromCollection: true
        },
        boost_specs: {
            name: "Boost specs",
            dataType: "array",
            hideFromCollection: true,
            of: {
                dataType: "map",
                properties: {
                    field: { dataType: "string", name: "Field" },
                    control_points: {
                        name: "Control points",
                        dataType: "array",
                        of: {
                            dataType: "map",
                            properties: {
                                value: { dataType: "number", name: "Value" },
                                boost: { dataType: "number", name: "Boost" }
                            }
                        }
                    }
                }
            }
        },
        created_at: {
            dataType: "date",
            name: "Created at",
            autoValue: "on_create"
        },
        updated_at: {
            dataType: "date",
            name: "Updated at",
            autoValue: "on_update"
        }
    },
    openEntityMode: "full_screen",
    Actions: DirectoryActions,
    callbacks: {
        onPreSave: async ({ values, context }: any) => {
            const processedValues = { ...values };

            const filters = values.filter_fields || {};
            const commitmentSlug = filters.commitment_type;
            const funcSlug = filters.function;
            const funcFocusSlug = filters.function_focus;
            const industrySlug = filters.industry;
            const locationInput = getLocationPathInput(filters.location);

            // Fetch labels for Title generation
            const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                fetchLabel("commitment_type", commitmentSlug, context.dataSource),
                fetchLabel("function", funcSlug, context.dataSource),
                fetchLabel("function_focus", funcFocusSlug, context.dataSource, funcSlug),
                fetchLabel("industry", industrySlug, context.dataSource)
            ]);

            const finalFuncLabel = funcFocusLabel || funcLabel;

            const generatedTitle = generateTitle(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));
            if (!values.title || values.title === "") {
                 processedValues.title = generatedTitle;
            }

            if (!values.meta_title || values.meta_title === "") {
                 processedValues.meta_title = generatedTitle;
            }

            const generatedDescription = generateDescription(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));
            if (generatedDescription && (!values.description || values.description.trim() === "")) {
                processedValues.description = generatedDescription;
            }

            const generatedMetaDescription = generateMetaDescription(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));
            if (generatedMetaDescription && (!values.meta_description || values.meta_description.trim() === "")) {
                processedValues.meta_description = generatedMetaDescription;
            }

            if (!values.override_clean_url) {
                processedValues.clean_url = generatePath(commitmentSlug, funcFocusSlug || funcSlug, industrySlug, locationInput);
            }

            // DR-408: an attached location must carry a positive range
            const location = filters.location;
            if (location && typeof location === "object" && location.ref
                && (typeof location.range !== "number" || location.range <= 0)) {
                throw new Error("Location range must be a positive number of miles");
            }

            return processedValues;
        }
    },
    additionalFields: [
        {
            key: "id",
            name: "ID",
            Builder: ({ entity }) => entity.id
        }
    ],
    propertiesOrder: [
        "title",
        "clean_url",
        "override_clean_url",
        "description",
        "meta_title",
        "meta_description",
        "enabled",
        "visible_in_menu",
        "page_size",
        "filter_fields",
        "created_at",
        "updated_at",
        "query",
        "filter",
        "boost_specs"
    ],
    entityActions: [
        {
            key: "open_live",
            name: "Open",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="currentColor">
                    <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h560v-280h80v280q0-33-23.5-56.5T760-120H200Zm188-212-56-56 372-372H560v-80h280v280h-80v-144L388-332Z"/>
                </svg>
            ),
            collapsed: false,
            onClick: ({ entity }: any) => {
                const cleanUrl = entity.values.clean_url || "";
                const frontendDomain = import.meta.env.VITE_FRONTEND_URL || "http://localhost:4321";
                const domain = frontendDomain.endsWith("/") ? frontendDomain.slice(0, -1) : frontendDomain;
                const path = cleanUrl.startsWith("/") ? cleanUrl.slice(1) : cleanUrl;
                const url = `${domain}/directory/${path}`;
                window.open(url, "_blank");
            }
        },
        {
            key: "regenerate_title",
            name: "Regenerate Title",
            icon: (
                <span className="form-action-text-button">
                    Regenerate Title
                </span>
            ),
            includeInForm: true,
            onClick: async ({ context, formContext }: any) => {
                if (!formContext) return;
                const values = formContext.values || {};
                const filters = values.filter_fields || {};
                const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                    fetchLabel("commitment_type", filters.commitment_type, context.dataSource),
                    fetchLabel("function", filters.function, context.dataSource),
                    fetchLabel("function_focus", filters.function_focus, context.dataSource, filters.function),
                    fetchLabel("industry", filters.industry, context.dataSource)
                ]);
                const finalFuncLabel = funcFocusLabel || funcLabel;
                const title = generateTitle(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));
                formContext.setFieldValue("title", title);
            }
        },
        {
            key: "regenerate_description",
            name: "Regenerate Description",
            icon: (
                <span className="form-action-text-button">
                    Regenerate Description
                </span>
            ),
            includeInForm: true,
            onClick: async ({ context, formContext }: any) => {
                if (!formContext) return;
                const values = formContext.values || {};
                const filters = values.filter_fields || {};
                const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                    fetchLabel("commitment_type", filters.commitment_type, context.dataSource),
                    fetchLabel("function", filters.function, context.dataSource),
                    fetchLabel("function_focus", filters.function_focus, context.dataSource, filters.function),
                    fetchLabel("industry", filters.industry, context.dataSource)
                ]);
                const finalFuncLabel = funcFocusLabel || funcLabel;
                const description = generateDescription(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));
                formContext.setFieldValue("description", description);
            }
        },
        {
            key: "regenerate_meta_title",
            name: "Regenerate Meta Title",
            icon: (
                <span className="form-action-text-button">
                    Regenerate Meta Title
                </span>
            ),
            includeInForm: true,
            onClick: async ({ context, formContext }: any) => {
                if (!formContext) return;
                const values = formContext.values || {};
                const filters = values.filter_fields || {};
                const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                    fetchLabel("commitment_type", filters.commitment_type, context.dataSource),
                    fetchLabel("function", filters.function, context.dataSource),
                    fetchLabel("function_focus", filters.function_focus, context.dataSource, filters.function),
                    fetchLabel("industry", filters.industry, context.dataSource)
                ]);
                const finalFuncLabel = funcFocusLabel || funcLabel;
                const title = generateTitle(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));
                formContext.setFieldValue("meta_title", title);
            }
        },
        {
            key: "regenerate_meta_description",
            name: "Regenerate Meta Description",
            icon: (
                <span className="form-action-text-button">
                    Regenerate Meta Description
                </span>
            ),
            includeInForm: true,
            onClick: async ({ context, formContext }: any) => {
                if (!formContext) return;
                const values = formContext.values || {};
                const filters = values.filter_fields || {};
                const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                    fetchLabel("commitment_type", filters.commitment_type, context.dataSource),
                    fetchLabel("function", filters.function, context.dataSource),
                    fetchLabel("function_focus", filters.function_focus, context.dataSource, filters.function),
                    fetchLabel("industry", filters.industry, context.dataSource)
                ]);
                const finalFuncLabel = funcFocusLabel || funcLabel;
                const description = generateMetaDescription(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));
                formContext.setFieldValue("meta_description", description);
            }
        },
        {
            key: "restore_path",
            name: "Restore Default Path",
            icon: (
                <span className="form-action-text-button">
                    Restore Default Path
                </span>
            ),
            includeInForm: true,
            onClick: ({ formContext }: any) => {
                if (!formContext) return;
                const values = formContext.values || {};
                const filters = values.filter_fields || {};
                const path = generatePath(filters.commitment_type, filters.function_focus || filters.function, filters.industry, getLocationPathInput(filters.location));
                formContext.setFieldValue("override_clean_url", false);
                formContext.setFieldValue("clean_url", path);
            }
        }
    ]
});

async function fetchLabel(fieldId: string, slug: string | undefined, dataSource: any, parentSlug?: string): Promise<string | undefined> {
    if (!slug) return undefined;
    try {
        const entity = await dataSource.fetchEntity({ 
            path: "field", 
            entityId: fieldId,
            collection: fieldCollection as any,
            useCache: true
        });
        const values = entity?.values as any;
        if (!values) return undefined;

        const lookup = values.key_lookups || values.key_lookup || {};
        
        let label: string | undefined = undefined;

        if (parentSlug) {
            let nested = lookup[parentSlug];
            if (!nested) {
                const foundParentKey = Object.keys(lookup).find(k => k.toLowerCase() === parentSlug.toLowerCase());
                if (foundParentKey) nested = lookup[foundParentKey];
            }
            if (nested && typeof nested === "object") {
                label = nested[slug];
            }
        } else {
            label = lookup[slug];
        }

        // Fallback to key_value array if label not found in lookup
        if (!label && Array.isArray(values.key_value)) {
            const item = values.key_value.find((i: any) => i.key === slug);
            if (item) label = item.label;
        }

        return label;
    } catch (e) {
        console.error(`Error fetching label for ${fieldId}/${slug}:`, e);
        return undefined;
    }
}

export function DirectoryActions({ context, selectionController, tableController }: {
    context: FireCMSContext;
    selectionController: SelectionController<any>;
    tableController: any;
}) {
    const sideEntityController = useSideEntityController();
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [isRegeneratingPaths, setIsRegeneratingPaths] = useState(false);
    const [isRegeneratingTitles, setIsRegeneratingTitles] = useState(false);
    const [isRegeneratingDescriptions, setIsRegeneratingDescriptions] = useState(false);
    const [isRegeneratingMetaTitles, setIsRegeneratingMetaTitles] = useState(false);
    const [isRegeneratingMetaDescriptions, setIsRegeneratingMetaDescriptions] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState<{
        message: string;
        onConfirm: () => void;
    } | null>(null);

    const selectedEntities = selectionController?.selectedEntities || [];
    const hasSelection = selectedEntities.length > 0;

    const allEntities = tableController?.data || [];
    const isAllSelected = allEntities.length > 0 && selectedEntities.length === allEntities.length;

    const handleToggleSelectAll = useCallback(() => {
        if (isAllSelected) {
            selectionController?.setSelectedEntities([]);
        } else {
            selectionController?.setSelectedEntities(allEntities);
        }
    }, [isAllSelected, selectionController, allEntities]);

    const handleRegeneratePaths = useCallback(async () => {
        if (!hasSelection) return;
        setIsRegeneratingPaths(true);
        const CHUNK_SIZE = 25;
        try {
            for (let i = 0; i < selectedEntities.length; i += CHUNK_SIZE) {
                const chunk = selectedEntities.slice(i, i + CHUNK_SIZE);
                const promises = chunk.map(entity => {
                    const values = entity.values || {};
                    const filters = values.filter_fields || {};
                    const newPath = generatePath(
                        filters.commitment_type,
                        filters.function_focus || filters.function,
                        filters.industry,
                        getLocationPathInput(filters.location)
                    );

                    return context.dataSource.saveEntity({
                        path: entity.path,
                        entityId: entity.id,
                        values: {
                            ...values,
                            clean_url: newPath,
                            override_clean_url: false
                        },
                        status: "existing"
                    });
                });
                const results = await Promise.allSettled(promises);
                const failures = results.filter(r => r.status === "rejected");
                if (failures.length > 0) {
                    const errorMsg = failures.map(f => (f as PromiseRejectedResult).reason?.message || String((f as PromiseRejectedResult).reason)).join("; ");
                    throw new Error(`${failures.length} saves failed in batch: ${errorMsg}`);
                }
            }
            selectionController.setSelectedEntities([]);
        } catch (err) {
            console.error("Error regenerating paths:", err);
        } finally {
            setIsRegeneratingPaths(false);
        }
    }, [hasSelection, selectedEntities, context.dataSource, selectionController]);

    const handleRegenerateTitles = useCallback(async () => {
        if (!hasSelection) return;
        setIsRegeneratingTitles(true);
        const CHUNK_SIZE = 25;
        try {
            for (let i = 0; i < selectedEntities.length; i += CHUNK_SIZE) {
                const chunk = selectedEntities.slice(i, i + CHUNK_SIZE);
                const promises = chunk.map(async (entity) => {
                    const values = entity.values || {};
                    const filters = values.filter_fields || {};

                    const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                        fetchLabel("commitment_type", filters.commitment_type, context.dataSource),
                        fetchLabel("function", filters.function, context.dataSource),
                        fetchLabel("function_focus", filters.function_focus, context.dataSource, filters.function),
                        fetchLabel("industry", filters.industry, context.dataSource)
                    ]);
                    const finalFuncLabel = funcFocusLabel || funcLabel;

                    const newTitle = generateTitle(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));

                    return context.dataSource.saveEntity({
                        path: entity.path,
                        entityId: entity.id,
                        values: {
                            ...values,
                            title: newTitle
                        },
                        status: "existing"
                    });
                });
                const results = await Promise.allSettled(promises);
                const failures = results.filter(r => r.status === "rejected");
                if (failures.length > 0) {
                    const errorMsg = failures.map(f => (f as PromiseRejectedResult).reason?.message || String((f as PromiseRejectedResult).reason)).join("; ");
                    throw new Error(`${failures.length} saves failed in batch: ${errorMsg}`);
                }
            }
            selectionController.setSelectedEntities([]);
        } catch (err) {
            console.error("Error regenerating titles:", err);
        } finally {
            setIsRegeneratingTitles(false);
        }
    }, [hasSelection, selectedEntities, context.dataSource, selectionController]);

    const handleRegenerateDescriptions = useCallback(async () => {
        if (!hasSelection) return;
        setConfirmConfig({
            message: `Are you sure you want to regenerate descriptions for ${selectedEntities.length} selected directories? This will overwrite any existing descriptions.`,
            onConfirm: async () => {
                setIsRegeneratingDescriptions(true);
                const CHUNK_SIZE = 25;
                try {
                    for (let i = 0; i < selectedEntities.length; i += CHUNK_SIZE) {
                        const chunk = selectedEntities.slice(i, i + CHUNK_SIZE);
                        const promises = chunk.map(async (entity) => {
                            const values = entity.values || {};
                            const filters = values.filter_fields || {};

                            const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                                fetchLabel("commitment_type", filters.commitment_type, context.dataSource),
                                fetchLabel("function", filters.function, context.dataSource),
                                fetchLabel("function_focus", filters.function_focus, context.dataSource, filters.function),
                                fetchLabel("industry", filters.industry, context.dataSource)
                            ]);
                            const finalFuncLabel = funcFocusLabel || funcLabel;

                            const newDescription = generateDescription(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));

                            return context.dataSource.saveEntity({
                                path: entity.path,
                                entityId: entity.id,
                                values: {
                                    ...values,
                                    description: newDescription
                                },
                                status: "existing"
                            });
                        });
                        const results = await Promise.allSettled(promises);
                        const failures = results.filter(r => r.status === "rejected");
                        if (failures.length > 0) {
                            const errorMsg = failures.map(f => (f as PromiseRejectedResult).reason?.message || String((f as PromiseRejectedResult).reason)).join("; ");
                            throw new Error(`${failures.length} saves failed in batch: ${errorMsg}`);
                        }
                    }
                    selectionController.setSelectedEntities([]);
                } catch (err) {
                    console.error("Error regenerating descriptions:", err);
                } finally {
                    setIsRegeneratingDescriptions(false);
                }
            }
        });
    }, [hasSelection, selectedEntities, context.dataSource, selectionController]);

    const handleRegenerateMetaTitles = useCallback(async () => {
        if (!hasSelection) return;
        setIsRegeneratingMetaTitles(true);
        const CHUNK_SIZE = 25;
        try {
            for (let i = 0; i < selectedEntities.length; i += CHUNK_SIZE) {
                const chunk = selectedEntities.slice(i, i + CHUNK_SIZE);
                const promises = chunk.map(async (entity) => {
                    const values = entity.values || {};
                    const filters = values.filter_fields || {};

                    const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                        fetchLabel("commitment_type", filters.commitment_type, context.dataSource),
                        fetchLabel("function", filters.function, context.dataSource),
                        fetchLabel("function_focus", filters.function_focus, context.dataSource, filters.function),
                        fetchLabel("industry", filters.industry, context.dataSource)
                    ]);
                    const finalFuncLabel = funcFocusLabel || funcLabel;

                    const newTitle = generateTitle(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));

                    return context.dataSource.saveEntity({
                        path: entity.path,
                        entityId: entity.id,
                        values: {
                            ...values,
                            meta_title: newTitle
                        },
                        status: "existing"
                    });
                });
                const results = await Promise.allSettled(promises);
                const failures = results.filter(r => r.status === "rejected");
                if (failures.length > 0) {
                    const errorMsg = failures.map(f => (f as PromiseRejectedResult).reason?.message || String((f as PromiseRejectedResult).reason)).join("; ");
                    throw new Error(`${failures.length} saves failed in batch: ${errorMsg}`);
                }
            }
            selectionController.setSelectedEntities([]);
        } catch (err) {
            console.error("Error regenerating meta titles:", err);
        } finally {
            setIsRegeneratingMetaTitles(false);
        }
    }, [hasSelection, selectedEntities, context.dataSource, selectionController]);

    const handleRegenerateMetaDescriptions = useCallback(async () => {
        if (!hasSelection) return;
        setConfirmConfig({
            message: `Are you sure you want to regenerate meta descriptions for ${selectedEntities.length} selected directories? This will overwrite any existing meta descriptions.`,
            onConfirm: async () => {
                setIsRegeneratingMetaDescriptions(true);
                const CHUNK_SIZE = 25;
                try {
                    for (let i = 0; i < selectedEntities.length; i += CHUNK_SIZE) {
                        const chunk = selectedEntities.slice(i, i + CHUNK_SIZE);
                        const promises = chunk.map(async (entity) => {
                            const values = entity.values || {};
                            const filters = values.filter_fields || {};

                            const [commitmentLabel, funcLabel, funcFocusLabel, industryLabel] = await Promise.all([
                                fetchLabel("commitment_type", filters.commitment_type, context.dataSource),
                                fetchLabel("function", filters.function, context.dataSource),
                                fetchLabel("function_focus", filters.function_focus, context.dataSource, filters.function),
                                fetchLabel("industry", filters.industry, context.dataSource)
                            ]);
                            const finalFuncLabel = funcFocusLabel || funcLabel;

                            const newDescription = generateMetaDescription(commitmentLabel, finalFuncLabel, industryLabel, getLocationDisplayName(filters.location));

                            return context.dataSource.saveEntity({
                                path: entity.path,
                                entityId: entity.id,
                                values: {
                                    ...values,
                                    meta_description: newDescription
                                },
                                status: "existing"
                            });
                        });
                        const results = await Promise.allSettled(promises);
                        const failures = results.filter(r => r.status === "rejected");
                        if (failures.length > 0) {
                            const errorMsg = failures.map(f => (f as PromiseRejectedResult).reason?.message || String((f as PromiseRejectedResult).reason)).join("; ");
                            throw new Error(`${failures.length} saves failed in batch: ${errorMsg}`);
                        }
                    }
                    selectionController.setSelectedEntities([]);
                } catch (err) {
                    console.error("Error regenerating meta descriptions:", err);
                } finally {
                    setIsRegeneratingMetaDescriptions(false);
                }
            }
        });
    }, [hasSelection, selectedEntities, context.dataSource, selectionController]);

    return (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Button 
                variant="filled" 
                size="small" 
                // DR-441 QA: navigating to "/c/directory/new" opens an entity view
                // for the literal id "new" — the first save creates directory/new,
                // and every later "Add Directory" click reopens that same document.
                // Opening the side entity panel without an entityId is FireCMS's
                // own new-entity flow (auto-generated id on save).
                onClick={() => sideEntityController.open({ path: "directory" })}
            >
                Add Directory
            </Button>
            <Button
                variant="outlined"
                size="small"
                onClick={() => setIsImportOpen(true)}
            >
                Bulk Import
            </Button>
            {isImportOpen && (
                <ImportDirectoriesModal
                    context={context}
                    onClose={() => setIsImportOpen(false)}
                />
            )}
            {allEntities.length > 0 && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleToggleSelectAll}
                    disabled={isRegeneratingPaths || isRegeneratingTitles || isRegeneratingDescriptions || isRegeneratingMetaTitles || isRegeneratingMetaDescriptions}
                >
                    {isAllSelected ? "Clear Selection" : `Select All (${allEntities.length})`}
                </Button>
            )}
            {hasSelection && (
                <>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRegeneratePaths}
                        disabled={isRegeneratingPaths || isRegeneratingTitles || isRegeneratingDescriptions || isRegeneratingMetaTitles || isRegeneratingMetaDescriptions}
                    >
                        {isRegeneratingPaths ? "Regenerating..." : `Regenerate Path (${selectedEntities.length})`}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRegenerateTitles}
                        disabled={isRegeneratingPaths || isRegeneratingTitles || isRegeneratingDescriptions || isRegeneratingMetaTitles || isRegeneratingMetaDescriptions}
                    >
                        {isRegeneratingTitles ? "Regenerating..." : `Regenerate Title (${selectedEntities.length})`}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRegenerateDescriptions}
                        disabled={isRegeneratingPaths || isRegeneratingTitles || isRegeneratingDescriptions || isRegeneratingMetaTitles || isRegeneratingMetaDescriptions}
                    >
                        {isRegeneratingDescriptions ? "Regenerating..." : `Regenerate Description (${selectedEntities.length})`}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRegenerateMetaTitles}
                        disabled={isRegeneratingPaths || isRegeneratingTitles || isRegeneratingDescriptions || isRegeneratingMetaTitles || isRegeneratingMetaDescriptions}
                    >
                        {isRegeneratingMetaTitles ? "Regenerating..." : `Regenerate Meta Title (${selectedEntities.length})`}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRegenerateMetaDescriptions}
                        disabled={isRegeneratingPaths || isRegeneratingTitles || isRegeneratingDescriptions || isRegeneratingMetaTitles || isRegeneratingMetaDescriptions}
                    >
                        {isRegeneratingMetaDescriptions ? "Regenerating..." : `Regenerate Meta Description (${selectedEntities.length})`}
                    </Button>
                </>
            )}
            {confirmConfig && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full border border-gray-100 overflow-hidden">
                        <div className="p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Are you sure?</h3>
                            <p className="text-sm text-gray-600">{confirmConfig.message}</p>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
                            <Button variant="outlined" size="small" onClick={() => setConfirmConfig(null)}>Cancel</Button>
                            <Button 
                                variant="filled" 
                                size="small" 
                                style={{ backgroundColor: "#4f46e5" }}
                                onClick={() => {
                                    confirmConfig.onConfirm();
                                    setConfirmConfig(null);
                                }}
                            >
                                Confirm
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
